"use client";
import React, { createContext, useEffect, useState } from "react";
import axios from "axios";
import {
  addCurrencyToStorage,
  getCurrencyFromStorage,
} from "@/app/utils/services/service/localStorageService";
import { COUNTRY_TO_CURRENCY_CODE } from "@/app/common/consts/currencies";
import { useRouter } from "@/i18n/navigation";
import { useSearchParams } from "next/navigation";
import { useLocale } from "next-intl";

type CurrencyOption = { label: string; value: string; type?: string };

export const CurrencyContext = createContext({
  currency: { label: "USD", value: "USD" } as CurrencyOption,
  setUpdateCurrency: (op: CurrencyOption) => {},
  currencies: [{ label: "USD", value: "USD" }] as unknown as CurrencyOption[],
  rate: 1,
  currencyCode: "",
});

const CurrencyProvider = ({ children }: { children: React.ReactNode }) => {
  const locale = useLocale();
  const savedCurrency = getCurrencyFromStorage();
  const router = useRouter();
  const searchParams = useSearchParams();
  const urlCurrency = searchParams.get("currency")?.toUpperCase();

  const [currency, setCurrency] = useState(
    urlCurrency
      ? {
          label: urlCurrency,
          value: urlCurrency,
        }
      : savedCurrency?.value && savedCurrency.value != "USD"
        ? savedCurrency
        : {
            label: "USD",
            value: "USD",
          }
  );
  const [updatedCurrency, setUpdateCurrency] = useState(
    urlCurrency
      ? {
          label: urlCurrency,
          value: urlCurrency,
        }
      : savedCurrency?.value && savedCurrency.value != "USD"
        ? savedCurrency
        : {
            label: "USD",
            value: "USD",
          }
  );
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([]);
  const [rate, setRate] = useState(1);

  useEffect(() => {
    fetchCurrencies();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());

    if (updatedCurrency?.value === "USD") {
      setRate(1);
      setCurrency(updatedCurrency);
      addCurrencyToStorage(JSON.stringify({ ...updatedCurrency }));
    } else {
      updateRate();
    }
    params.set("currency", updatedCurrency.value);
    router.replace(`?${params.toString()}`, { scroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedCurrency]);

  async function updateRate() {
    try {
      const { data } = await axios.get(
        `/api/currency/convert?currencyCode=${updatedCurrency.value}&lang=${locale}`
      );
      if (data.rate) {
        setRate(Number(data?.rate));
        setCurrency(updatedCurrency);
        addCurrencyToStorage(JSON.stringify(updatedCurrency));
        const params = new URLSearchParams(searchParams.toString());
        params.set("currency", updatedCurrency.value);
        router.replace(`?${params.toString()}`, { scroll: false });
      } else {
        setCurrency(savedCurrency);
      }
    } catch (err) {
      setCurrency(savedCurrency);
    }
  }

  async function fetchCurrencies() {
    try {
      const res = await fetch(`/api/currency?lang=${locale}`);
      const data = await res.json();
      if (
        savedCurrency &&
        savedCurrency.value === "USD" &&
        savedCurrency?.isDefault
      ) {
        getUserLocationInfo();
      }

      setCurrencies(data?.currencies);
    } catch (error) {
      console.log("Error fetching currencies", error);
    }
  }

  const getUserLocationInfo = async () => {
    try {
      const res = await fetch(`/api/currency/user-currency?lang=${locale}`);
      const data = await res.json();
      const currencyCode = COUNTRY_TO_CURRENCY_CODE[data?.countryCode];
      if (!currencyCode) return;
      setUpdateCurrency({
        label: currencyCode,
        value: currencyCode,
      });
    } catch (error) {
      console.error("Failed to fetch IP/location:", error);
    }
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        currencies,
        rate,
        currencyCode: currency?.value,
        setUpdateCurrency,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyProvider;
